import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../cn.js'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  loading?: boolean
  fullWidth?: boolean
  children: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
  /**
   * Gold FILL with navy text — 8.9:1 (PLAN.md §9.3).
   * Gold as *text* on a light surface is 1.6:1 and is forbidden.
   */
  primary: 'bg-accent text-brand hover:brightness-95 active:brightness-90',
  /** Blue is the only palette colour legible as both fill and text. */
  secondary: 'bg-info text-white hover:brightness-110 active:brightness-95',
  ghost: 'bg-transparent text-brand hover:bg-surface-alt',
}

export function Button({
  variant = 'primary',
  loading = false,
  fullWidth = true,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      // A loading button must stay focusable but reject clicks; `disabled`
      // would move focus and lose the user's place mid-submit.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg px-4 font-semibold transition',
        // 44px minimum touch target — the funnel is predominantly mobile (§11.6).
        'min-h-[44px] py-3',
        fullWidth && 'w-full',
        VARIANTS[variant],
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  )
}
